import { FC, useContext, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { sectionRoutes, StudentSectionSettingKeys, StudentSectionSettingTypeCodes } from 'const';
import { RendererContext } from 'context';
import { useAppSelector, useDataLoad, useStudentNavigation, useUserFunctionality } from 'hooks';
import { commonTheme } from 'portfolio3/styles/theme';
import { visibilitySettingsSelector } from 'selectors';
import { getDataLoadRequests } from 'utils';
import { findTargetVisibilitySetting } from 'utils/localVisibilitySettingsService';

import ProforientationResultsDrawer from '../../components/ProforientationResultsDrawer';
import ProforientationResultsPopup from '../../components/ProforientationResultsPopup';
import { proforientationResultFoundSelector, proforientationVisitedSelector } from '../../model/selectors';

const ProforientationResultsPopupContainer: FC = () => {
  const { currentRenderer } = useContext(RendererContext);
  const { isStudent } = useUserFunctionality();
  const { navigateToSection } = useStudentNavigation();
  const classLevel = useAppSelector((state) => state.currentStudent.classLevel);
  const isSmallScreen = useMediaQuery(commonTheme.breakpoints.down('commonSm'));
  const personalSettings = useAppSelector(visibilitySettingsSelector);
  const professionTypeCode = StudentSectionSettingTypeCodes[StudentSectionSettingKeys.PROFESSION];
  const subsectionTypeCode = StudentSectionSettingTypeCodes.professionProforientation;
  const professionVisibilitySetting = findTargetVisibilitySetting(personalSettings, [
    professionTypeCode,
    subsectionTypeCode,
  ]);
  const [isResultsPopupOpen, setResultsPopupOpen] = useState(true);
  const isProfessionVisible = professionVisibilitySetting?.computedVisibility ?? false;
  const isStudentWithProfession = classLevel ? classLevel.includes('9') : false;
  const isWidgetVisible = isStudent && currentRenderer === 'main' && isProfessionVisible;

  useDataLoad({
    shouldLoad: isWidgetVisible,
    requests: getDataLoadRequests().professionProforientation,
  });

  const isVisited = useAppSelector(proforientationVisitedSelector);
  const isFound = useAppSelector(proforientationResultFoundSelector);

  const isPopupOpenAndVisited =
    isStudentWithProfession && isProfessionVisible && isVisited === false && isFound && isResultsPopupOpen;

  const handleResultsPopupClose = () => setResultsPopupOpen(false);

  const handleRedirectToProfessionPage = () => {
    handleResultsPopupClose();
    navigateToSection(sectionRoutes.PROFESSION);
  };

  if (!isWidgetVisible) {
    return null;
  }

  if (isSmallScreen) {
    return (
      <ProforientationResultsDrawer
        onAction={handleRedirectToProfessionPage}
        isOpen={isPopupOpenAndVisited}
        onClose={handleResultsPopupClose}
      />
    );
  }

  return (
    <ProforientationResultsPopup
      onAction={handleRedirectToProfessionPage}
      isOpen={isPopupOpenAndVisited}
      onClose={handleResultsPopupClose}
    />
  );
};

export default ProforientationResultsPopupContainer;
